import { useState, useEffect, useRef, useCallback } from 'react';
import uk from 'date-fns/locale/uk';
import { CalendarToday, Clear } from '@mui/icons-material';
import { Box, TextField, Portal } from '@mui/material';
import { format, parse, addMonths, subMonths, addYears, subYears } from 'date-fns';

import { DatePickerContent } from './DatePickerContent';
import { TimePickerSection } from './TimePickerSection';
import { DatePickerActions } from './DatePickerActions';

import { CustomDatePickerProps } from './CustomDatePicker.types';

import './CustomDatePicker.css';

/**
 * CustomDatePicker - A comprehensive date/time picker component with multiple view modes
 *
 * Features:
 * - Three operational modes: date-only, time-only, and datetime
 * - Multiple view modes: day, month, year, and time
 * - Smart date reset: preserves selected day when changing month/year if valid
 * - Timezone-aware: sets noon for date-only mode to avoid timezone offset issues
 * - Portal rendering to appear above dialogs and modals
 * - Click-outside detection for automatic closing
 * - Ukrainian localization with date-fns
 * - Controlled component with onChange callback
 * - Automatic view reset to 'day' when picker opens
 * - Flexible output formats: Date object, datetime-local string, or date string
 * - Soft auto-formatting: allows free editing, formats on blur or when complete
 * - Preserves space between date and time
 *
 * Mode Detection:
 * - Date-only: showTime=false
 * - Time-only: showTime=true && dateFormat='dd.MM.yyyy'
 * - DateTime: showTime=true && dateFormat!='dd.MM.yyyy'
 *
 * @component
 * @param {CustomDatePickerProps} props - Component props
 * @returns {JSX.Element} Date picker with input field and popup
 */

export const CustomDatePicker = ({
  value,
  onChange,
  format: dateFormat = 'dd.MM.yyyy',
  className,
  showTime = false,
  timeFormat = 'HH:mm',
  includeSeconds = false,
  disabled = false,
  hideClearButton = false,
  outputFormat = 'iso'
}: CustomDatePickerProps) => {
  // Safely validate date
  const isValidDate = (date: any): boolean => {
    if (!date) return false;
    if (date instanceof Date) return !isNaN(date.getTime());
    const parsed = new Date(date);
    return !isNaN(parsed.getTime());
  };

  const isTimeOnly = showTime && dateFormat === 'dd.MM.yyyy';
  const isDateTimeMode = showTime && dateFormat !== 'dd.MM.yyyy';
  const validValue = isValidDate(value) ? value : null;

  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState<'day' | 'month' | 'year' | 'time'>('day');
  const [displayDate, setDisplayDate] = useState<Date>(validValue ? new Date(validValue) : new Date());
  const [inputValue, setInputValue] = useState(
    validValue ? format(new Date(validValue), isTimeOnly ? timeFormat : dateFormat, { locale: uk }) : ''
  );
  const [hours, setHours] = useState(validValue ? new Date(validValue).getHours() : 0);
  const [minutes, setMinutes] = useState(validValue ? new Date(validValue).getMinutes() : 0);
  const [seconds, setSeconds] = useState(validValue ? new Date(validValue).getSeconds() : 0);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const MIN_POPUP_WIDTH = 280;
  const EDGE_GAP = 32;
  const [popupPos, setPopupPos] = useState({ top: 0, left: 0, width: 0 });
  const isMobile = window.innerWidth <= 768;

  /**
   * Converts a Date object to the specified output format
   */
  const formatOutput = useCallback(
    (date: Date | null): Date | string | null => {
      if (!date || !isValidDate(date)) return null;

      if (outputFormat === 'datetime-local') {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');

        if (includeSeconds) {
          return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
        }
        return `${year}-${month}-${day}T${hours}:${minutes}`;
      } else if (outputFormat === 'date') {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }
      return date;
    },
    [outputFormat, includeSeconds]
  );

  /**
   * Parses output format string back to Date object
   */
  const parseFromOutput = useCallback((value: string | Date | null): Date | null => {
    if (!value) return null;
    if (value instanceof Date) return isValidDate(value) ? value : null;

    const date = new Date(value);
    return isValidDate(date) ? date : null;
  }, []);

  /**
   * Parses input string to Date, padding with zeros for incomplete input
   */
  const parseInputToDate = useCallback(
    (input: string): Date | null => {
      if (!input) return null;

      const targetFormat = isTimeOnly ? timeFormat : dateFormat;

      let padded = input;
      while (padded.length < targetFormat.length) {
        padded += '0';
      }

      try {
        if (isTimeOnly) {
          const parsed = parse(padded, timeFormat, new Date());
          return isValidDate(parsed) ? parsed : null;
        } else {
          const parsed = parse(padded, dateFormat, new Date(), { locale: uk });
          return isValidDate(parsed) ? parsed : null;
        }
      } catch {
        return null;
      }
    },
    [isTimeOnly, timeFormat, dateFormat]
  );

  /**
   * Auto-formats input by inserting separators at correct positions
   * Preserves space between date and time in datetime mode
   */
  const autoFormatInput = useCallback(
    (input: string): string => {
      const targetFormat = isTimeOnly ? timeFormat : dateFormat;
      let formatted = '';
      let digitIndex = 0;

      for (let i = 0; i < targetFormat.length && digitIndex < input.length; i++) {
        const formatChar = targetFormat[i];
        if (
          formatChar === 'd' ||
          formatChar === 'M' ||
          formatChar === 'y' ||
          formatChar === 'H' ||
          formatChar === 'm' ||
          formatChar === 's'
        ) {
          formatted += input[digitIndex];
          digitIndex++;
        } else if (formatChar === ' ') {
          // Preserve space between date and time
          formatted += ' ';
        } else {
          formatted += formatChar;
        }
      }

      return formatted;
    },
    [isTimeOnly, timeFormat, dateFormat]
  );

  /**
   * Validates and restricts individual time/date components
   */
  const validateAndRestrictInput = useCallback(
    (digitsOnly: string): string => {
      const targetFormat = isTimeOnly ? timeFormat : dateFormat;
      const maxDigits = targetFormat.replace(/[^dMyHms]/g, '').length;

      // Pad to max length first
      let padded = digitsOnly;
      while (padded.length < maxDigits) {
        padded += '0';
      }

      // Extract individual components based on format
      let result = '';
      let digitIndex = 0;

      for (let i = 0; i < targetFormat.length && digitIndex < padded.length; i++) {
        const formatChar = targetFormat[i];

        if (formatChar === 'd') {
          // Day: 01-31
          const nextDigit = padded[digitIndex];
          if (digitIndex === 0) {
            // First digit of day: 0-3
            result += Math.min(parseInt(nextDigit), 3).toString();
          } else if (digitIndex === 1) {
            // Second digit of day: depends on first digit
            const firstDigit = parseInt(result[result.length - 1]);
            if (firstDigit === 3) {
              // If first digit is 3, second can only be 0-1 (30-31)
              result += Math.min(parseInt(nextDigit), 1).toString();
            } else {
              // Otherwise 0-9
              result += nextDigit;
            }
          }
          digitIndex++;
        } else if (formatChar === 'M') {
          // Month: 01-12
          const nextDigit = padded[digitIndex];
          if (digitIndex === 0 || (digitIndex > 0 && targetFormat[i - 1] !== 'M')) {
            // First digit of month: 0-1
            result += Math.min(parseInt(nextDigit), 1).toString();
          } else {
            // Second digit of month: depends on first digit
            const firstDigit = parseInt(result[result.length - 1]);
            if (firstDigit === 1) {
              // If first digit is 1, second can only be 0-2 (10-12)
              result += Math.min(parseInt(nextDigit), 2).toString();
            } else if (firstDigit === 0) {
              // If first digit is 0, second can be 1-9 (01-09)
              result += Math.max(Math.min(parseInt(nextDigit), 9), 1).toString();
            } else {
              result += nextDigit;
            }
          }
          digitIndex++;
        } else if (formatChar === 'y') {
          // Year: any 4 digits
          result += padded[digitIndex];
          digitIndex++;
        } else if (formatChar === 'H') {
          // Hour: 00-23
          const nextDigit = padded[digitIndex];
          if (digitIndex === 0 || (digitIndex > 0 && targetFormat[i - 1] !== 'H')) {
            // First digit of hour: 0-2
            result += Math.min(parseInt(nextDigit), 2).toString();
          } else {
            // Second digit of hour: depends on first digit
            const firstDigit = parseInt(result[result.length - 1]);
            if (firstDigit === 2) {
              // If first digit is 2, second can only be 0-3 (20-23)
              result += Math.min(parseInt(nextDigit), 3).toString();
            } else {
              // Otherwise 0-9
              result += nextDigit;
            }
          }
          digitIndex++;
        } else if (formatChar === 'm') {
          // Minute: 00-59
          const nextDigit = padded[digitIndex];
          if (digitIndex === 0 || (digitIndex > 0 && targetFormat[i - 1] !== 'm')) {
            // First digit of minute: 0-5
            result += Math.min(parseInt(nextDigit), 5).toString();
          } else {
            // Second digit of minute: 0-9
            result += nextDigit;
          }
          digitIndex++;
        } else if (formatChar === 's') {
          // Second: 00-59
          const nextDigit = padded[digitIndex];
          if (digitIndex === 0 || (digitIndex > 0 && targetFormat[i - 1] !== 's')) {
            // First digit of second: 0-5
            result += Math.min(parseInt(nextDigit), 5).toString();
          } else {
            // Second digit of second: 0-9
            result += nextDigit;
          }
          digitIndex++;
        } else if (formatChar === ' ') {
          // Space
          result += ' ';
        } else {
          // Separator (dot, colon, etc.)
          result += formatChar;
        }
      }

      return result;
    },
    [isTimeOnly, timeFormat, dateFormat]
  );

  const calcPopupPos = useCallback((rect: DOMRect) => {
    const width = Math.max(rect.width, MIN_POPUP_WIDTH);
    const maxLeft = window.innerWidth - width - EDGE_GAP;
    const left = Math.min(rect.left + window.scrollX, maxLeft + window.scrollX);
    return { top: rect.bottom + window.scrollY, left: Math.max(left, EDGE_GAP), width };
  }, []);

  useEffect(() => {
    if (isOpen && wrapperRef.current) {
      setPopupPos(calcPopupPos(wrapperRef.current.getBoundingClientRect()));
    }
  }, [isOpen, calcPopupPos]);

  useEffect(() => {
    const updatePosition = () => {
      if (isOpen && wrapperRef.current) {
        requestAnimationFrame(() => {
          if (wrapperRef.current) {
            setPopupPos(calcPopupPos(wrapperRef.current.getBoundingClientRect()));
          }
        });
      }
    };

    if (isOpen) {
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [isOpen, calcPopupPos]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const inWrapper = wrapperRef.current?.contains(target);
      const inPopup = popupRef.current?.contains(target);
      if (
        wrapperRef.current &&
        !inWrapper &&
        !inPopup
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen && !isMobile) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  useEffect(() => {
    if (validValue) {
      const dateValue = parseFromOutput(validValue);
      if (dateValue) {
        setInputValue(prev => {
          if (prev === '') return prev;
          return format(dateValue, isTimeOnly ? timeFormat : dateFormat, { locale: uk });
        });
        setDisplayDate(dateValue);
        setHours(dateValue.getHours());
        setMinutes(dateValue.getMinutes());
        setSeconds(dateValue.getSeconds());
      }
    }
  }, [validValue, isTimeOnly, timeFormat, dateFormat, parseFromOutput]);

  useEffect(() => {
    if (isOpen && inputValue) {
      const parsedDate = parseInputToDate(inputValue);
      if (parsedDate && isValidDate(parsedDate)) {
        setDisplayDate(parsedDate);
        setHours(parsedDate.getHours());
        setMinutes(parsedDate.getMinutes());
        setSeconds(parsedDate.getSeconds());
      }
    }
  }, [isOpen, inputValue, parseInputToDate]);

  useEffect(() => {
    return () => {
      setIsOpen(false);
    };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    const targetFormat = isTimeOnly ? timeFormat : dateFormat;

    // Extract only digits
    const digitsOnly = val.replace(/[^0-9]/g, '');

    // Limit to max digits in format
    const maxDigits = targetFormat.replace(/[^dMyHms]/g, '').length;
    const limitedDigits = digitsOnly.substring(0, maxDigits);

    // Auto-format with separators (no validation during typing)
    const formatted = autoFormatInput(limitedDigits);

    setInputValue(formatted);
  };

  const handleInputBlur = () => {
    const targetFormat = isTimeOnly ? timeFormat : dateFormat;

    // Extract only digits from input
    const digitsOnly = inputValue.replace(/[^0-9]/g, '');

    if (digitsOnly.length > 0) {
      // Validate and restrict, then pad with zeros
      const validated = validateAndRestrictInput(digitsOnly);
      const digitsFromValidated = validated.replace(/[^0-9]/g, '');

      // Pad with zeros to complete the format
      let paddedDigits = digitsFromValidated;
      const maxDigits = targetFormat.replace(/[^dMyHms]/g, '').length;
      while (paddedDigits.length < maxDigits) {
        paddedDigits += '0';
      }

      // Auto-format with separators
      const formatted = autoFormatInput(paddedDigits);

      // Validate the formatted string
      try {
        if (isTimeOnly) {
          const parsed = parse(formatted, timeFormat, new Date());
          if (isValidDate(parsed)) {
            setInputValue(formatted);
            onChange(formatOutput(parsed));
            setHours(parsed.getHours());
            setMinutes(parsed.getMinutes());
            setSeconds(parsed.getSeconds());
            setDisplayDate(parsed);
          } else {
            // Invalid time, revert to previous value
            setInputValue(format(displayDate, isTimeOnly ? timeFormat : dateFormat, { locale: uk }));
          }
        } else {
          const isDateOnly = !showTime;
          const parsed = parse(formatted, dateFormat, new Date(), { locale: uk });
          if (isValidDate(parsed)) {
            setInputValue(formatted);
            const newDate = new Date(parsed);
            if (!isDateOnly) {
              newDate.setHours(hours, minutes, includeSeconds ? seconds : 0, 0);
            }
            onChange(formatOutput(newDate));
            setDisplayDate(newDate);
          } else {
            // Invalid date, revert to previous value
            setInputValue(format(displayDate, isTimeOnly ? timeFormat : dateFormat, { locale: uk }));
          }
        }
      } catch {
        // Invalid format, revert to previous value
        setInputValue(format(displayDate, isTimeOnly ? timeFormat : dateFormat, { locale: uk }));
      }
    } else {
      onChange(null);
    }
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const targetFormat = isTimeOnly ? timeFormat : dateFormat;

    // Auto-format and parse on Enter
    if (e.key === 'Enter') {
      const digitsOnly = inputValue.replace(/[^0-9]/g, '');
      if (digitsOnly.length > 0) {
        // Validate and restrict, then pad with zeros
        const validated = validateAndRestrictInput(digitsOnly);
        const digitsFromValidated = validated.replace(/[^0-9]/g, '');

        // Pad with zeros to complete the format
        let paddedDigits = digitsFromValidated;
        const maxDigits = targetFormat.replace(/[^dMyHms]/g, '').length;
        while (paddedDigits.length < maxDigits) {
          paddedDigits += '0';
        }

        const formatted = autoFormatInput(paddedDigits);

        // Validate the formatted string
        try {
          if (isTimeOnly) {
            const parsed = parse(formatted, timeFormat, new Date());
            if (isValidDate(parsed)) {
              setInputValue(formatted);
              onChange(formatOutput(parsed));
              setHours(parsed.getHours());
              setMinutes(parsed.getMinutes());
              setSeconds(parsed.getSeconds());
              setDisplayDate(parsed);
            } else {
              // Invalid time, revert to previous value
              setInputValue(format(displayDate, isTimeOnly ? timeFormat : dateFormat, { locale: uk }));
            }
          } else {
            const isDateOnly = !showTime;
            const parsed = parse(formatted, dateFormat, new Date(), { locale: uk });
            if (isValidDate(parsed)) {
              setInputValue(formatted);
              const newDate = new Date(parsed);
              if (!isDateOnly) {
                newDate.setHours(hours, minutes, includeSeconds ? seconds : 0, 0);
              }
              onChange(formatOutput(newDate));
              setDisplayDate(newDate);
            } else {
              // Invalid date, revert to previous value
              setInputValue(format(displayDate, isTimeOnly ? timeFormat : dateFormat, { locale: uk }));
            }
          }
        } catch {
          // Invalid format, revert to previous value
          setInputValue(format(displayDate, isTimeOnly ? timeFormat : dateFormat, { locale: uk }));
        }
      }
    }
  };

  /**
   * Builds a date from individual components using Date constructor
   * This approach avoids timezone offset issues by directly setting date/time values
   */
  const buildDateFromComponents = useCallback(
    (year: number, month: number, date: number, hour: number, minute: number, second: number) => {
      const newDate = new Date(year, month, date, hour, minute, second, 0);
      return newDate;
    },
    []
  );

  const handleDayClick = (day: Date) => {
    const targetHour = !showTime ? 12 : hours;
    const newDate = buildDateFromComponents(
      day.getFullYear(),
      day.getMonth(),
      day.getDate(),
      targetHour,
      !showTime ? 0 : minutes,
      !showTime ? 0 : includeSeconds ? seconds : 0
    );
    onChange(formatOutput(newDate));
    setDisplayDate(newDate);
    setInputValue(format(newDate, isTimeOnly ? timeFormat : dateFormat, { locale: uk }));
  };

  const handleMonthClick = (month: number) => {
    const currentDay = displayDate.getDate();
    const testDate = new Date(displayDate.getFullYear(), month, currentDay);
    const dayInNewMonth = testDate.getMonth() === month ? currentDay : 1;
    const targetHour = !showTime ? 12 : hours;

    const newDate = buildDateFromComponents(
      displayDate.getFullYear(),
      month,
      dayInNewMonth,
      targetHour,
      !showTime ? 0 : minutes,
      !showTime ? 0 : includeSeconds ? seconds : 0
    );
    onChange(formatOutput(newDate));
    setDisplayDate(newDate);
    setInputValue(format(newDate, isTimeOnly ? timeFormat : dateFormat, { locale: uk }));
  };

  const handleYearClick = (year: number) => {
    const currentDay = displayDate.getDate();
    const currentMonth = displayDate.getMonth();
    const testDate = new Date(year, currentMonth, currentDay);
    const dayInNewYear = testDate.getMonth() === currentMonth ? currentDay : 1;
    const targetHour = !showTime ? 12 : hours;

    const newDate = buildDateFromComponents(
      year,
      currentMonth,
      dayInNewYear,
      targetHour,
      !showTime ? 0 : minutes,
      !showTime ? 0 : includeSeconds ? seconds : 0
    );
    onChange(formatOutput(newDate));
    setDisplayDate(newDate);
    setInputValue(format(newDate, isTimeOnly ? timeFormat : dateFormat, { locale: uk }));
  };

  const handleHourChange = (newHour: number) => {
    const newDate = buildDateFromComponents(
      displayDate.getFullYear(),
      displayDate.getMonth(),
      displayDate.getDate(),
      newHour,
      minutes,
      includeSeconds ? seconds : 0
    );
    setHours(newHour);
    onChange(formatOutput(newDate));
    setInputValue(format(newDate, isTimeOnly ? timeFormat : dateFormat, { locale: uk }));
  };

  const handleMinuteChange = (newMinute: number) => {
    const newDate = buildDateFromComponents(
      displayDate.getFullYear(),
      displayDate.getMonth(),
      displayDate.getDate(),
      hours,
      newMinute,
      includeSeconds ? seconds : 0
    );
    setMinutes(newMinute);
    onChange(formatOutput(newDate));
    setInputValue(format(newDate, isTimeOnly ? timeFormat : dateFormat, { locale: uk }));
  };

  const handleSecondChange = (newSecond: number) => {
    const newDate = buildDateFromComponents(
      displayDate.getFullYear(),
      displayDate.getMonth(),
      displayDate.getDate(),
      hours,
      minutes,
      newSecond
    );
    setSeconds(newSecond);
    onChange(formatOutput(newDate));
    setInputValue(format(newDate, isTimeOnly ? timeFormat : dateFormat, { locale: uk }));
  };

  const handleClear = () => {
    setInputValue('');
    setHours(0);
    setMinutes(0);
    setSeconds(0);
    onChange(null);
    setIsOpen(false);
  };

  const handleAccept = () => {
    setIsOpen(false);
  };

  const handleNavigation = useCallback(
    (direction: 'prev' | 'next') => {
      setDisplayDate(prev => {
        if (view === 'day') {
          return direction === 'prev' ? subMonths(prev, 1) : addMonths(prev, 1);
        } else if (view === 'month') {
          return direction === 'prev' ? subYears(prev, 1) : addYears(prev, 1);
        } else {
          return direction === 'prev' ? subYears(prev, 10) : addYears(prev, 10);
        }
      });
    },
    [view]
  );

  const renderTimeSection = () => (
    <TimePickerSection
      hours={hours}
      minutes={minutes}
      seconds={seconds}
      includeSeconds={includeSeconds}
      isTimeOnly={isTimeOnly}
      onHourChange={handleHourChange}
      onMinuteChange={handleMinuteChange}
      onSecondChange={handleSecondChange}
    />
  );

  return (
    <Box ref={wrapperRef} className={`custom-date-picker-wrapper ${className || ''}`}>
      <TextField
        value={inputValue}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        onKeyDown={handleInputKeyDown}
        onFocus={() => !disabled && setIsOpen(true)}
        size="small"
        className="custom-date-picker-input"
        placeholder={isTimeOnly ? timeFormat : dateFormat}
        disabled={disabled}
        autoComplete="off"
      />
      {inputValue && !disabled && !hideClearButton && (
        <Clear
          className="custom-date-picker-clear-icon"
          onClick={e => {
            e.stopPropagation();
            handleClear();
          }}
        />
      )}
      <CalendarToday
        className={`custom-date-picker-icon ${disabled ? 'disabled' : ''}`}
        onClick={e => {
          if (disabled) return;
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
      />

      {isOpen && (
        <Portal container={document.body}>
          <Box
            ref={popupRef}
            className={`custom-date-picker-popup${isMobile ? ' mobile-centered' : ''}`}
            onClick={e => e.stopPropagation()}
            onMouseDown={e => e.stopPropagation()}
            onTouchStart={e => e.stopPropagation()}
            style={isMobile ? { zIndex: 9999 } : {
              position: 'absolute',
              top: `${popupPos.top}px`,
              left: `${popupPos.left}px`,
              width: `${popupPos.width}px`,
              zIndex: 9999
            }}
          >
            {isDateTimeMode ? (
              <DatePickerContent
                key={`${displayDate.getFullYear()}-${displayDate.getMonth()}-${displayDate.getDate()}`}
                view={view}
                displayDate={displayDate}
                value={value}
                onViewChange={setView}
                onNavigate={handleNavigation}
                onDayClick={handleDayClick}
                onMonthClick={handleMonthClick}
                onYearClick={handleYearClick}
                showTimeButton={true}
              />
            ) : (
              !isTimeOnly && (
                <DatePickerContent
                  key={`${displayDate.getFullYear()}-${displayDate.getMonth()}-${displayDate.getDate()}`}
                  view={view}
                  displayDate={displayDate}
                  value={value}
                  onViewChange={setView}
                  onNavigate={handleNavigation}
                  onDayClick={handleDayClick}
                  onMonthClick={handleMonthClick}
                  onYearClick={handleYearClick}
                />
              )
            )}

            {isDateTimeMode && view === 'time' && renderTimeSection()}
            {isTimeOnly && renderTimeSection()}

            <DatePickerActions onClear={handleClear} onAccept={handleAccept} />
          </Box>
        </Portal>
      )}
    </Box>
  );
};
